import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import type { FunnelABTestDecisionRow, FunnelReportsDashboardData } from '../types';
import { resolveDecisionVerdict } from '../decisionVerdict';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';

/**
 * Operator-first "Needs attention" strip (QW1, reworked after user review).
 * The strip carries ONLY items that require a decision or block one:
 *   1. sample-ratio mismatch on an A/B (broken split — results untrustworthy)
 *   2. A/B tests ready to decide
 * Per-scope data-quality and identity problems are NOT repeated here — they
 * are marked inline on the affected rows and explained once in the bottom
 * "Warnings & data trust" section (`ReportWarningsSection`).
 * Empty + loaded → an explicit "all clear" line, so silence is a statement.
 */

export interface ReportAttentionItem {
  key: string;
  rank: number;
  severity: 'error' | 'warning' | 'info' | 'success';
  title: string;
  detail?: string;
  /** R5-4: deep link — SRM items open the Experiments hub (card badge), decide items scroll to the scorecards. */
  onOpen?: () => void;
}

function decisionLabel(row: FunnelABTestDecisionRow): string {
  const test = row.testName ?? row.testId;
  const iteration = row.iterationName ?? row.iterationId;
  return `${test} · ${iteration}`;
}

export interface ReportAttentionActions {
  /** SRM items open the Experiments hub, where the card header carries the SRM badge (R3-2). */
  openExperiments?: () => void;
  /** Decide items scroll to the decision scorecards on this page. */
  openDecisions?: () => void;
}

export function deriveReportAttentionItems(
  _dashboard: FunnelReportsDashboardData | null | undefined,
  decisions: FunnelABTestDecisionRow[],
  actions: ReportAttentionActions = {},
): ReportAttentionItem[] {
  const items: ReportAttentionItem[] = [];

  for (const row of decisions) {
    const verdict = resolveDecisionVerdict(row);
    if (verdict.sampleRatioMismatch) {
      items.push({
        key: `srm-${row.testId}-${row.iterationId}`,
        rank: 1,
        severity: 'error',
        title: `Sample ratio mismatch — ${decisionLabel(row)}`,
        detail: 'The traffic split is broken; pause decisions on this test until the split is fixed.',
        onOpen: actions.openExperiments,
      });
    } else if (verdict.allocationMetadataUnknown) {
      items.push({
        key: `allocation-${row.testId}-${row.iterationId}`,
        rank: 1,
        severity: 'warning',
        title: `Allocation metadata unavailable — ${decisionLabel(row)}`,
        detail: row.srm?.reasonCode === 'mixed_allocation_epoch'
          ? 'This window mixes allocation epochs; choose one epoch before making a decision.'
          : 'The expected allocation cannot be verified; decisions are paused without claiming the traffic split is broken.',
        onOpen: actions.openExperiments,
      });
    } else if (row.status === 'ready_to_choose' || row.status === 'winner_declared') {
      items.push({
        key: `decide-${row.testId}-${row.iterationId}`,
        rank: 2,
        severity: 'info',
        title: `Ready to decide — ${decisionLabel(row)}`,
        detail: verdict.reason,
        onOpen: actions.openDecisions,
      });
    }
  }

  return items.sort((a, b) => a.rank - b.rank);
}

interface ReportAttentionStripProps {
  dashboard: FunnelReportsDashboardData | null | undefined;
  decisions: FunnelABTestDecisionRow[];
  /** Render nothing until the first dashboard payload has arrived. */
  loaded: boolean;
  /** R5-4: per-kind deep links; absent → items render without an Open action. */
  actions?: ReportAttentionActions;
}

export const ReportAttentionStrip: React.FC<ReportAttentionStripProps> = ({ dashboard, decisions, loaded, actions }) => {
  const items = React.useMemo(
    () => deriveReportAttentionItems(dashboard, decisions, actions),
    [dashboard, decisions, actions],
  );

  if (!loaded) return null;

  // C4 (studio-reports redesign): the permanent «All clear» banner is deleted.
  // It was on screen whenever nothing needed attention — which is most loads —
  // so it carried no signal and cost a row every time. Nothing to decide now
  // renders as nothing. R5-AC-002's deep link is unaffected: it lives on the
  // ITEMS below, which still render whenever there is something to act on.
  if (!items.length) return null;

  return (
    <Box data-testid={FUNNEL_REPORTS_TEST_IDS.attentionStrip}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Needs attention</Typography>
      <Stack spacing={0.75}>
        {items.map(item => (
          <Alert
            key={item.key}
            severity={item.severity}
            data-testid={FUNNEL_REPORTS_TEST_IDS.attentionItem(item.key)}
            action={item.onOpen ? (
              <Button
                color="inherit"
                size="small"
                data-testid={FUNNEL_REPORTS_TEST_IDS.attentionItemOpen(item.key)}
                onClick={item.onOpen}
              >
                Open
              </Button>
            ) : undefined}
          >
            <strong>{item.title}</strong>
            {item.detail ? ` — ${item.detail}` : ''}
          </Alert>
        ))}
      </Stack>
    </Box>
  );
};
